import React from 'react';
import Icon from '../../../components/AppIcon';

import { Checkbox } from '../../../components/ui/Checkbox';

const TargetAudienceSection = ({ formData, errors, onChange }) => {
  const personaOptions = [
    { value: 'decision-makers', label: 'Business Decision Makers', description: 'C-level executives, managers, directors' },
    { value: 'end-users', label: 'End Users', description: 'Individual consumers or product users' },
    { value: 'influencers', label: 'Industry Influencers', description: 'Thought leaders, bloggers, social media personalities' },
    { value: 'technical', label: 'Technical Professionals', description: 'Developers, engineers, IT specialists' },
    { value: 'procurement', label: 'Procurement Teams', description: 'Purchasing managers, buyers' },
    { value: 'students', label: 'Students & Learners', description: 'Educational audience seeking knowledge' }
  ];

  const marketSegmentOptions = [
    { value: 'enterprise', label: 'Enterprise', description: 'Large corporations (1000+ employees)' },
    { value: 'mid-market', label: 'Mid-Market', description: 'Medium-sized businesses (100-999 employees)' },
    { value: 'smb', label: 'Small & Medium Business', description: 'SMBs (10-99 employees)' },
    { value: 'startups', label: 'Startups', description: 'Early-stage companies' },
    { value: 'solopreneurs', label: 'Solopreneurs', description: 'Individual entrepreneurs' },
    { value: 'consumers', label: 'Direct Consumers', description: 'B2C individual customers' }
  ];

  const handlePersonaChange = (value, checked) => {
    const current = formData?.customerPersonas || [];
    const updated = checked
      ? [...current, value]
      : current?.filter(v => v !== value);
    onChange('customerPersonas', updated);
  };

  const handleSegmentChange = (value, checked) => {
    const current = formData?.marketSegments || [];
    const updated = checked
      ? [...current, value]
      : current?.filter(v => v !== value);
    onChange('marketSegments', updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Users" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground font-heading">
            Target Audience Definition
          </h2>
          <p className="text-muted-foreground mt-1">
            Define who your customers are to enable highly targeted marketing campaigns
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Target Audience Description
            <span className="text-error ml-1">*</span>
          </label>
          <p className="text-sm text-muted-foreground mb-2">
            Describe your ideal customer in detail (demographics, behaviors, pain points)
          </p>
          <textarea
            placeholder="e.g., Small business owners aged 30-50 who struggle with time management and need productivity tools to streamline operations..."
            value={formData?.targetAudience || ''}
            onChange={(e) => onChange('targetAudience', e?.target?.value)}
            rows={4}
            className={`
              w-full px-4 py-3 rounded-lg border bg-background text-foreground
              focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
              transition-all duration-250 resize-none
              ${errors?.targetAudience ? 'border-error' : 'border-input'}
            `}
          />
          {errors?.targetAudience && (
            <p className="text-sm text-error mt-2">{errors?.targetAudience}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Customer Personas
            <span className="text-muted-foreground ml-2 font-normal">(Select all that apply)</span>
          </label>
          <p className="text-sm text-muted-foreground mb-3">
            Who are the key decision-makers and users you're targeting?
          </p>
          <div className="space-y-3">
            {personaOptions?.map((option) => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                description={option?.description}
                checked={(formData?.customerPersonas || [])?.includes(option?.value)}
                onChange={(e) => handlePersonaChange(option?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Market Segments
            <span className="text-muted-foreground ml-2 font-normal">(Select all that apply)</span>
          </label>
          <p className="text-sm text-muted-foreground mb-3">
            Which market segments do you serve?
          </p>
          <div className="space-y-3">
            {marketSegmentOptions?.map((option) => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                description={option?.description}
                checked={(formData?.marketSegments || [])?.includes(option?.value)}
                onChange={(e) => handleSegmentChange(option?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="p-4 bg-muted/50 rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Sparkles" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">AI-Powered Personalization:</p>
            <p>
              The more detailed your audience information, the better our Marketing Agent can create personalized
              campaigns, email templates, and ad copy that resonate with your specific customer segments.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetAudienceSection;